/** Minimal fields needed to mirror chat model selector ordering. */
export type ChatOrderableModel = {
  slug: string
  display_name: string
  position: number
  is_featured: boolean
  is_active?: boolean
  is_hidden?: boolean
}

export function sortModelsByPosition<T extends ChatOrderableModel>(
  models: T[],
): T[] {
  return [...models].sort((a, b) => {
    const posDiff = a.position - b.position
    if (posDiff !== 0) return posDiff
    return a.slug.localeCompare(b.slug)
  })
}

/** Models shown in chat / the topbar selector (active, not admin-hidden). */
export function filterChatListableModels<T extends ChatOrderableModel>(
  models: T[],
): T[] {
  return sortModelsByPosition(
    models.filter((model) => model.is_active !== false && !model.is_hidden),
  )
}

/**
 * Split listable models for the chat dropdown: featured in the main panel,
 * non-featured under “More models”. Order within each group follows `position`.
 */
export function splitModelsForChatDropdown<T extends ChatOrderableModel>(
  models: T[],
): { featured: T[]; more: T[] } {
  const listable = filterChatListableModels(models)
  return {
    featured: listable.filter((model) => model.is_featured),
    more: listable.filter((model) => !model.is_featured),
  }
}

export type ChatSelectorPreviewEntry = {
  display_name: string
  slug: string
  section: 'featured' | 'more'
}

/** Flat preview of chat selector order (featured block, then more block). */
export function buildChatSelectorPreview<T extends ChatOrderableModel>(
  models: T[],
): ChatSelectorPreviewEntry[] {
  const { featured, more } = splitModelsForChatDropdown(models)
  return [
    ...featured.map((model) => ({
      display_name: model.display_name,
      slug: model.slug,
      section: 'featured' as const,
    })),
    ...more.map((model) => ({
      display_name: model.display_name,
      slug: model.slug,
      section: 'more' as const,
    })),
  ]
}
